import { useCallback, useState } from 'react'

import type {
  ExportDownload,
  ExportFilters,
  ExportStatus,
} from 'features/projects/features/export/types'

const DEFAULT_FILENAME = 'dataset_export.csv'

export type PerformExport = (filters: ExportFilters) => Promise<ExportDownload>

type UseExportDatasetsResult = {
  status: ExportStatus
  filename: string | null
  errorMessage: string | null
  triggerExport: (filters: ExportFilters) => Promise<void>
  reset: () => void
}

/**
 * Owns the export lifecycle: idle → preparing → success/error.
 *
 * `performExport(filters)` is the caller-supplied async that hits whichever
 * endpoint the page wants — the homepage passes `service.exportDatasets`
 * (bulk), the per-project page passes a closure over the project UUID
 * calling `service.exportProjectDataset(projUuid, filters)`. The hook
 * stays endpoint-agnostic.
 *
 * On `triggerExport(filters)`:
 *   1. Awaits `performExport(filters)` and reads the returned download
 *      (Blob + the BE's filename hint; falls back to DEFAULT_FILENAME).
 *   2. Synthesizes a downloadable `<a>` element, clicks it, revokes the
 *      object URL. The download happens in parallel with the success
 *      visual moment owned by the Container/Modal.
 *
 * Distinguishes 401 / 403 / 5xx errors so the View can render specific copy.
 */
export function useExportDatasets(
  performExport: PerformExport,
): UseExportDatasetsResult {
  const [status, setStatus] = useState<ExportStatus>('idle')
  const [filename, setFilename] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const reset = useCallback(() => {
    setStatus('idle')
    setFilename(null)
    setErrorMessage(null)
  }, [])

  const triggerExport = useCallback(
    async (filters: ExportFilters) => {
      setStatus('preparing')
      setErrorMessage(null)
      setFilename(null)

      try {
        const download = await performExport(filters)
        const resolvedName = download.filename ?? DEFAULT_FILENAME
        triggerBrowserDownload(download.blob, resolvedName)
        setFilename(resolvedName)
        setStatus('success')
      } catch (err) {
        const friendly = await toFriendlyError(err)
        setErrorMessage(friendly)
        setStatus('error')
      }
    },
    [performExport],
  )

  return { status, filename, errorMessage, triggerExport, reset }
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

async function toFriendlyError(err: unknown): Promise<string> {
  if (isAxiosError(err)) {
    const status = err.response?.status
    if (status === 401) {
      return 'Admin role required.'
    }
    if (status === 403) {
      return 'This export is restricted to Yogi staff.'
    }
    if (status && status >= 500) {
      return 'Server error — please try again in a moment.'
    }
  }
  return 'Export failed. Please try again.'
}

type AxiosErrorShape = { isAxiosError: boolean; response?: { status?: number } }

function isAxiosError(err: unknown): err is AxiosErrorShape {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { isAxiosError?: boolean }).isAxiosError === true
  )
}
